import { memo, useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import capitalize from "lodash/capitalize";
import { useDispatch, useSelector } from "react-redux";

import { ListContentLoader, StructureModal } from "@similarweb/ui-components";

import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";
import { translate } from "app/filters/i18nFilter";
import { getContentStyles } from "app/pages/benchmark-tracker/components/Report/CompetitorsModal/config";
import { SubTitle } from "app/pages/benchmark-tracker/components/Report/CompetitorsModal/SubTitle";
import { metrics } from "app/pages/benchmark-tracker/components/Report/Tab/config";
import type { BenchmarkColumnSchema } from "../MetricElements/TableView/config";
import { useModalTableData } from "app/pages/benchmark-tracker/hooks/useModalTableData";
import { getCompetitorsEffect } from "app/pages/benchmark-tracker/store/effects/report";
import { selectCompetitorsModalIsLoading } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import { DownloadExcelDropdown } from "../DownloadExcelDropdown/DownloadExcelDropdown";
import TableView from "../MetricElements/TableView/TableView";

import { StyledLoaderWrapper, StyledModalContainer } from "./styles";
import type { CompetitorsModalSchema } from "./type";

const CompetitorsModal = ({ onClose, isOpen, metricName, tabName, isRelative, isInverted }: CompetitorsModalSchema) => {
    const {
        params: { key: benchmarkId, country, duration, granularity },
    } = useCurrentStateAndParams();
    const [columnsConfigs, setColumnsConfigs] = useState<Partial<Record<BenchmarkMetricName, BenchmarkColumnSchema[] | undefined>>>({});

    const dispatch = useDispatch();

    const isLoading = useSelector(selectCompetitorsModalIsLoading);
    const tableData = useModalTableData(metricName, isRelative ?? false, isInverted ?? false);

    useEffect(() => {
        if (!columnsConfigs[metricName]) {
            const columnsConfig = metrics[tabName]?.find(({ name }) => name === metricName)?.modalColumnsConfig;

            setColumnsConfigs({
                ...columnsConfigs,
                [metricName]: columnsConfig?.({ granularity, isRelative, metricName }),
            });
        }
    }, [granularity, metricName, tabName, isRelative]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        dispatch(getCompetitorsEffect(metricName, benchmarkId, country, duration, granularity));
    }, [benchmarkId, country, dispatch, duration, granularity, isOpen, metricName]);

    return (
        <StructureModal
            title={translate(`bt.metric.modal.title.${metricName}`)}
            isOpen={isOpen}
            customStyles={{
                content: getContentStyles(metricName),
            }}
            shouldCloseOnEsc
            contentPadding="0"
            showHeaderDivider={false}
            shouldCloseOnOverlayClick
            mainButton={{
                label: capitalize(translate("si.common.close")),
                onClick: onClose,
                type: "primary",
                isDisabled: false,
                tooltipEnabled: false,
            }}
        >
            {metricName === BenchmarkMetricName.trafficShare ? (
                <DownloadExcelDropdown
                    duration={duration}
                    benchmarkId={benchmarkId}
                    granularity={granularity}
                    totalCount={tableData?.length || 0}
                    country={country}
                />
            ) : null}

            <StyledModalContainer>
                <>
                    <SubTitle metricName={metricName} />

                    {isLoading ? (
                        <StyledLoaderWrapper>
                            <ListContentLoader />
                        </StyledLoaderWrapper>
                    ) : (
                        <TableView
                            tableData={tableData ?? []}
                            columnsConfig={columnsConfigs[metricName] ?? []}
                            isLoading={isLoading}
                        />
                    )}
                </>
            </StyledModalContainer>
        </StructureModal>
    );
};

export default memo(CompetitorsModal);
